'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useQuery } from '@apollo/client';
import { GET_ALL_PAGES } from '@/queries/queries';
import { TfiClose } from "react-icons/tfi";
import { usePathname } from 'next/navigation';
import { useGlobalData } from '@/lib/api';
import DynamicLogo from '../DynamicLogo';
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from 'next/navigation';
interface Page {
  slug: string;
  short_description?: string;
}

interface PagesData {
  pages: Page[];
}

type HeaderMenuItem = {
  name: string;
  link: string;
  image?: {
    url: string;
    alternativeText?: string;
  };
};

// Define the type for site_details items
type SiteDetailItem = {
  children?: {
    type?: 'text' | 'link';
    text?: string;
    url?: string;
    children?: { text: string }[];
  }[];
};

type Header = {
  enquire_menu_label: string;
  enquire_menu_link?: any;
  right_side_text?: any;
  menu: HeaderMenuItem[];
  left_side_text: string;
  site_details: SiteDetailItem[];
};

export type Global = {
  site_name: string;
  site_description: string;
  site_logo?: { url: string; alternativeText?: string };
  site_favicon?: { url: string };
  header: Header;
  global?: any;
};

interface HeaderProps {
  globalData: Global;
}

export default function Header({ globalData }: HeaderProps) {
  const { data, loading, error } = useQuery<PagesData>(GET_ALL_PAGES);
  const homePage = data?.pages?.find((page) => page.slug === 'home');
  const [hoveredMenuIndex, setHoveredMenuIndex] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { global } = useGlobalData();
  const router = useRouter();
  const headerData = globalData.header;
  const pathname = usePathname();

  useEffect(() => {
    function handleResize() {
      setIsDesktop(window.innerWidth >= 768);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDesktopMenuOpen(false);
  }, [pathname]);

  const openMenu = () => {
    if (isDesktop) {
      setDesktopMenuOpen(true);
    } else {
      setMenuOpen(true);
    }
  };
  useEffect(() => {
    const body = document.querySelector('html');
    if (!body) return;

    if (desktopMenuOpen) {
      body.classList.add('menu-open'); // Better than `hidden` to preserve layout
    } else {
      body.classList.remove('menu-open');
    }
  }, [desktopMenuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    setDesktopMenuOpen(false);
  };
  const handleLinkClick = (url?: string) => {
    closeMenu();
    if (url) {
      setTimeout(() => {
        router.push(url);
      }, 700);
    }
  };

  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };
  const currentPath = usePathname();
  const isHomePage = currentPath === '/' || currentPath === '/home';
  const defaultMessage =
    'Elevating everyday living and redefining modern lifestyles with impactful and innovative forever homes.';
  return (
    <>
      <AnimatePresence mode="wait">
        {/* Overlay */}
        {(menuOpen || desktopMenuOpen) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[98] bg-black bg-opacity-50 duration-300 transition-all"
            onClick={closeMenu}
            aria-hidden="true"
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
      {/* Header */}
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.header
            key="default-header"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={fadeVariants}
            transition={{ duration: 0.3 }}
            className="absolute z-[99] pe-0 top-0 left-0 right-0 py-0 p-4 md:p-5 bg-transparent border-b border-[#2B332A]/40 md:border-none"
          >
            <div className="container-main mx-auto flex justify-between items-center md:items-start">
              <div className="text-[16px] font-light max-w-[300px] leading-relaxed">
                {!loading && !error && (
                  <>
                    {isHomePage ? (
                      homePage?.short_description || (
                        <Link href="/" onClick={() => handleLinkClick('/')}>
                          <div className="w-[210px] md:w-[auto] pt-4 md:pt-0 text-[16px] font-light leading-[23px] indent-10 text-start">
                            {defaultMessage}
                          </div>
                        </Link>
                      )
                    ) : (
                      <Link href="/" onClick={() => handleLinkClick('/')}>
                        <div className="text-xl md:text-[22px] leading-none logo-header">
                          {global?.site_logo?.url && (
                            <DynamicLogo svgUrl={global.site_logo.url} alt={global.site_name} />
                          )}
                        </div>
                      </Link>
                    )}
                  </>
                )}
              </div>
              <div className="md:flex items-center space-x-6 hidden leading-none">
                <button
                  onClick={() => handleLinkClick(headerData.enquire_menu_link)}
                  className="text-[16px] font-light"
                >
                  {headerData.enquire_menu_label}
                </button>
                <button
                  type="button"
                  className="text-[16px] font-light"
                  onClick={openMenu}
                  aria-label="Open menu"
                >
                  Menu
                </button>
              </div>
              <button
                type="button"
                onClick={openMenu}
                className="text-[#e5e1d5] md:hidden toggleicon border-l border-[#2B332A]/40"
                aria-label="Open mobile menu"
              />
            </div>
          </motion.header>
        </AnimatePresence>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-[100] flex flex-col md:p-5 overflow-y-auto transform transition-transform duration-500 ease-in-out will-change-transform bg-[#2B332A] text-white md:hidden ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-title"
      >
        <div className="flex justify-between items-center border-b border-[#B6C380]/40">
          <button
            className="text-[20px] font-light leading-none ps-4 menuLogo"
            onClick={() => handleLinkClick('/')}
          >
            <span id="mobile-menu-title">
              {global?.site_logo?.url && (
                <DynamicLogo svgUrl={global.site_logo.url} alt={global.site_name} />
              )}
            </span>
          </button>
          <button
            type="button"
            onClick={closeMenu}
            className="text-[16px] font-light w-[60px] h-[60px] text-center flex items-center justify-center border-l border-[#B6C380]/40"
            aria-label="Close menu"
          >
            <TfiClose />
          </button>
        </div>

        {global?.header?.right_side_text && (
          <Link
            href="#"
            className="mt-4 text-base font-light text-[#E5E1D5] hover:text-[#B6C380]"
            onClick={closeMenu}
          >
            {global.header.right_side_text}
          </Link>
        )}

        <nav className="flex flex-col md:gap-4 text-xl font-light border-t border-[#B6C380]/40 pt-8 leading-none">
          {headerData.menu.map((item, index) => (
            <Link
              href={item.link}
              key={index}
              onClick={closeMenu}
              className="border-b py-4 px-4 font-light border-[#B6C380]/40"
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="flex-grow" />
        <address className="text-[16px] font-light text-[#a1a68b] not-italic">
          <p className=" text-[#B6C380] px-4 py-4 border-t border-b border-[#B6C380]/40 leading-none">
            {headerData.left_side_text}
          </p>
          <div className="px-4 py-4 text-[#E5E1D5] font-light">
            {headerData.site_details.map((item, index) => (
              <p key={index}>
                {item.children?.map((child, i) => {
                  if (child.type === 'text') {
                    return child.text;
                  } else if (child.type === 'link') {
                    return (
                      <a key={i} href={child.url}>
                        {child.children?.[0]?.text}
                      </a>
                    );
                  }
                  return null;
                })}
              </p>
            ))}
          </div>
        </address>
      </div>

      {/* Desktop Menu */}
      <div
        className={`hidden md:flex inset-x-0 top-0 z-[999] overflow-hidden text-white bg-[#2B332A]  ${
          desktopMenuOpen ? 'relative h-[355px]' : 'relative h-0'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="desktop-menu-title"
        style={{
          transitionProperty: 'height, padding',
          transitionDuration: '600ms',
          transitionTimingFunction: 'cubic-bezier(0.77, 0, 0.175, 1)',
        }}
      >
        <div className="w-[29.5%] px-5 my-5 flex flex-col justify-between border-r border-[#B6C380]/40 h-[300px] left-col">
          <AnimatePresence mode="wait">
            {desktopMenuOpen && (
              <motion.div
                key="default-header"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={fadeVariants}
                transition={{ duration: 0.3 }}
              >
                <Link href="/" className="menuLogo" onClick={() => handleLinkClick('/')}>
                  {global?.site_logo?.url && (
                    <DynamicLogo svgUrl={global.site_logo.url} alt={global.site_name} />
                  )}
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
          <p className="text-[#B6C380] text-base font-light leading-none">
            {' '}
            {headerData.left_side_text}
          </p>
        </div>

        <address className="w-1/3 px-4 my-5 border-r border-[#B6C380]/40 flex flex-col justify-end space-y-3 font-light leading-none text-base text-[#E5E1D5] not-italic h-[300px]">
          {headerData.site_details.map((item, index) => (
            <p key={index}>
              {item.children?.map((child, i) => {
                if (child.type === 'text') {
                  return child.text;
                } else if (child.type === 'link') {
                  return (
                    <a className="hover:text-[#B6C380]" key={i} href={child.url}>
                      {child.children?.[0]?.text}
                    </a>
                  );
                }
                return null;
              })}
            </p>
          ))}
        </address>
        <div className="w-1/3 px-4 flex my-5 flex-col justify-between h-[300px]">
          <nav className="flex flex-col gap-2 text-[16px] lg:text-[22px] font-light leading-none text-[#E5E1D5]">
            {headerData.menu.map((item, index) => (
              <button
                key={index}
                // href={item.link}
                onClick={() => handleLinkClick(item.link)}
                onMouseEnter={() => setHoveredMenuIndex(index)}
                onMouseLeave={() => setHoveredMenuIndex(null)}
                className="hover:text-[#B6C380] text-left"
              >
                {item.name}
              </button>
            ))}
          </nav>
          {global?.header?.right_side_text && (
            <Link
              href="#"
              className="mt-4 text-base font-light text-[#E5E1D5] hover:text-[#B6C380]"
              onClick={closeMenu}
            >
              {global.header.right_side_text}
            </Link>
          )}
        </div>

        <div className="w-1/3 xl:w-2/12 my-5 px-4 text-base font-light border-l border-[#B6C380]/40 flex flex-col justify-between h-[300px]">
          <AnimatePresence mode="wait">
            {desktopMenuOpen && (
              <motion.div
                key="menu-buttons"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={fadeVariants}
                transition={{ duration: 0.3 }}
                className="flex justify-end space-x-6 leading-none px-1"
              >
                <button
                  className="hover:text-[#B6C380] text-[#E5E1D5] text-[16px] font-light"
                  onClick={() => handleLinkClick(headerData.enquire_menu_link)}
                >
                  Enquire
                </button>
                <button
                  type="button"
                  className="hover:text-[#B6C380] text-[#E5E1D5] text-[16px] font-light"
                  onClick={closeMenu}
                  aria-label="Close menu"
                >
                  Close
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          {headerData.menu.map((item, index) => {
            return hoveredMenuIndex === index && item.image?.url ? (
              <div
                className="mt-4 w-full flex justify-center transition-all"
                key={index}
                style={{ transitionProperty: 'opacity' }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className={`object-cover block w-full top-0 ms-auto left-0 ${headerData ? 'opacity-1 transition-all' : 'opacity-0 transition-all'}`}
                  src={item.image.url}
                  alt={item.image.alternativeText || item.name}
                  style={{ maxHeight: '230px', maxWidth: '180px' }}
                />
              </div>
            ) : null;
          })}
        </div>
      </div>
    </>
  );
}